/**
 * RestartActionType - Action permettant de recommencer l'aventure depuis le début
 * Identique à Flutter RestartActionType
 */

import {ActionType} from '../../types/ActionType';
import {GameState, GameStateStatus, GameStateType} from '../../../models/GameState';
import {PlayerStep} from '../../../models/PlayerStep';
import {PlayerNotification} from '../../../models/PlayerNotification';
import {createGameData} from '../../../models/GameData';
import {GameDataExtraField} from '../../../models/GameDataExtraField';
import playerService from '../../../services/playerService';
import adventureService from '../../../services/adventureService';
import sessionBridgeService from '../../../services/sessionBridgeService';
import GameStateHelper from '../../../helpers/GameStateHelper';
import {Platform} from 'react-native';

export class RestartActionType implements ActionType {
  typeName = 'restart';

  async execute(
    gameState: GameState,
    currentStep: PlayerStep,
    params?: {[key: string]: any},
  ): Promise<PlayerNotification[]> {
    console.log('[RestartActionType] Executing restart action');
    const notifications: PlayerNotification[] = [];

    try {
      const adventureURI = gameState.uniqueURI;
      console.log(`[RestartActionType] Loading adventure: ${adventureURI}`);
      
      // EN MODE ÉDITION : utiliser loadEditingPlayerAdventure (API) au lieu de loadPlayerAdventure (DB locale)
      const isEditingMode = Platform.OS === 'web' && typeof window !== 'undefined' && (window.opener !== null || sessionBridgeService.isInPopup());
      
      let currentAdventure;
      if (isEditingMode) {
        console.log(`[RestartActionType] Editing mode detected, loading adventure from API`);
        currentAdventure = await adventureService.loadEditingPlayerAdventure(adventureURI);
      } else {
        console.log(`[RestartActionType] Normal mode, loading adventure from local DB`);
        currentAdventure = await adventureService.loadPlayerAdventure(adventureURI);
      }
      
      if (!currentAdventure) {
        throw new Error('Adventure not found');
      }

      const startNumero = currentAdventure.startNumero || 1;
      const adventureType = currentAdventure.type || 'SIMPLE';
      
      console.log(`[RestartActionType] Adventure type: ${adventureType}`);
      console.log(`[RestartActionType] Adventure startNumero: ${startNumero}`);

      // Charger l'étape de départ (playerService.loadPlayerStep gère déjà le mode édition)
      const startingStep = await playerService.loadPlayerStep(adventureURI, startNumero);
      if (!startingStep) {
        throw new Error(`Starting step not found for adventure ${currentAdventure.name}`);
      }

      console.log(`[RestartActionType] Starting step loaded: numero=${startingStep.numero}`);

      // Réinitialiser le gameState (comme GameStateHelper.resetGameState)
      // Réinitialiser toutes les données du jeu
      const resetGameData = createGameData(startNumero, startNumero);
      
      // Réinitialiser les adventureExtraFields selon le type d'aventure (COMME À L'INIT INITIALE)
      // Recalculer les extraFields depuis l'aventure avec les formules (1D+6, etc.)
      // Utilise ceux de l'aventure si disponibles, sinon fallback sur les defaults
      const adventureExtraFields = GameStateHelper.initAdventureExtraFields(currentAdventure.extraFields, adventureType);
      console.log(`[RestartActionType] Reinitialized ${adventureExtraFields.length} adventure extraFields`);
      console.log(`[RestartActionType] Reinitialized ${adventureExtraFields.length} adventure extraFields`);
      resetGameData.adventureExtraFields = adventureExtraFields;

      // Mettre à jour le gameState
      gameState.data = resetGameData;
      gameState.data.currentStepNumber = startingStep.numero;
      gameState.state = GameStateStatus.PLAYING;
      gameState.type = GameStateType.NORMAL;
      
      console.log('[RestartActionType] GameState reinitialized:');
      console.log(`[RestartActionType] - currentStepNumber: ${gameState.data.currentStepNumber}`);
      console.log(`[RestartActionType] - state: ${gameState.state}`);
      console.log(`[RestartActionType] Restart completed successfully`);
    } catch (error) {
      console.error('[RestartActionType] Restart action failed:', error);
      throw error;
    }

    return notifications;
  }
}
